<template>
	<!-- 有子菜单或模块时，使用 el-submenu -->
	<el-submenu
		v-if="hasChildrenItems"
		:index="index"
		popper-class="surf-nav-submenu-popper"
		class="surf-nav-submenu"
		:popper-append-to-body="false"
		:popper-options="{ boundariesElement: 'viewport', removeOnDestroy: true }"
	>
		<template #title>
			<span class="surf-nav-menu-title">{{ menu.name }}</span>
			<SvgIcons name="down" class="surf-nav-submenu-arrow" />
		</template>
		<!-- 递归渲染子菜单 -->
		<NavMenuItemRenderer
			v-for="subMenu in menu.pageMenuVOS"
			:key="subMenu.uuid"
			:menu="subMenu"
			:index="subMenu.uuid"
			:is-sub-menu="true"
			@click="$emit('click', $event)"
		/>
		<!-- 模块列表 -->
		<template v-if="menu.menuType !== 1">
			<el-menu-item
				v-for="module in menu.moduleVOS"
				:key="module.moduleId"
				:index="`module-${module.moduleId}`"
				class="surf-nav-module-item"
				@click="handleModuleClick(module)"
			>
				<span class="surf-nav-menu-title">{{ module.name }}</span>
			</el-menu-item>
		</template>
	</el-submenu>
	<!-- 没有子菜单和模块时，使用 el-menu-item -->
	<el-menu-item v-else :index="index" class="surf-nav-menu-item" @click="handleMenuClick">
		<span class="surf-nav-menu-title">{{ menu.name }}</span>
	</el-menu-item>
</template>

<script>
export default {
	name: 'NavMenuItemRenderer'
};
</script>
<script setup>
import { computed } from 'vue';
import { checkChildrenMenus, checkChildrenModules, isOverviewMenu, resolveDefaultMenu } from './useNavMenu';
import NavMenuItemRenderer from './NavMenuItemRenderer.vue';

const props = defineProps({
	menu: {
		type: Object,
		required: true
	},
	index: {
		type: String,
		required: true
	},
	// 是否为子菜单（用于区分一级菜单和子菜单的点击处理）
	isSubMenu: {
		type: Boolean,
		default: false
	}
});

const emit = defineEmits(['click']);

const hasSubMenus = computed(() => checkChildrenMenus(props.menu));
const hasModules = computed(() => checkChildrenModules(props.menu));
const hasChildrenItems = computed(() => {
	if (isOverviewMenu(props.menu)) return false;
	return hasSubMenus.value || hasModules.value;
});

/**
 * 处理菜单点击事件
 * 一级菜单：如果菜单有子菜单，解析默认菜单并触发导航
 * 子菜单：直接传递，不需要解析默认菜单
 */
const handleMenuClick = () => {
	// 子菜单直接传递，不需要解析默认菜单
	if (props.isSubMenu) {
		emit('click', { menu: props.menu });
		return;
	}
	// 一级菜单：如果有子菜单，解析默认菜单
	const defaultMenu = resolveDefaultMenu(props.menu);
	emit('click', { menu: defaultMenu });
};

/**
 * 处理模块点击事件
 */
const handleModuleClick = module => {
	emit('click', { menu: props.menu, module });
};
</script>

<style lang="less" scoped>
// ============================================
// 组件自身样式
// ============================================

.surf-nav-submenu,
.surf-nav-menu-item {
	height: 100%;
}

.surf-nav-submenu-arrow {
	flex-shrink: 0;
	margin-left: 4px;
	color: rgba(var(--surf-inverse-color-rgb), 0.6) !important;
	transition: all 0.3s ease;
}
</style>

<style lang="less">
// ============================================
// 展开菜单样式（Popover 内的样式）
// ============================================
.surf-nav-submenu-popper {
	// Popover 容器样式
	.el-menu--popup {
		padding: 4px;
		min-width: 120px;
		max-width: 320px;
		border-radius: 2px;
		border: 0.5px solid rgba(var(--surf-primary-color-rgb), 0.3);
		background: var(--surf-secondary-color);
		box-shadow: 0 10px 20px 0 rgba(var(--surf-primary-color-rgb), 0.4);
	}
	.el-menu--popup-bottom-start {
		margin-top: 0;
	}
	.el-menu--popup-right-start {
		margin-left: 4px;
		margin-right: 4px;
	}

	// 移除菜单项底部下划线
	.el-menu--popup .el-menu-item.el-menu-item::after {
		display: none;
	}

	// 嵌套子菜单样式（权重高于最外层菜单项样式）
	.el-menu--popup .el-submenu.el-submenu {
		.el-submenu__title {
			background-color: transparent;

			// 隐藏 Element UI 默认箭头（必须使用 !important 覆盖内联样式）
			.el-submenu__icon-arrow {
				display: none !important;
			}

			// 展开菜单中的图标：默认旋转 90 度
			.surf-nav-submenu-arrow {
				transform: rotate(270deg) !important;
			}
		}
	}
}

.el-submenu .el-menu-item {
	min-width: 160px !important;
}

.surf-nav-submenu.el-submenu {
	.el-submenu__title {
		&:hover {
			.surf-nav-submenu-arrow {
				transform: rotate(180deg);
				color: var(--surf-inverse-color);
			}
		}
	}

	.surf-nav-submenu-popper {
		.surf-nav-menu-title {
			max-width: 160px;
			overflow: hidden;
			text-overflow: ellipsis;
			white-space: nowrap;
		}

		// 下拉菜单项基础样式
		.el-menu--popup .el-menu-item.el-menu-item,
		.el-menu--popup .el-submenu.el-submenu {
			padding: 4px 12px;
			background: transparent;
			color: rgba(var(--surf-inverse-color-rgb), 0.6);
			height: auto;
			line-height: 24px;
			transition: color 0.3s ease, background 0.3s ease;

			&:hover {
				border-radius: 2px;
				background: rgba(var(--surf-inverse-color-rgb), 0.1);
			}
		}
	}
}
</style>
